import { objToUrl } from ".";
import { baseUrl, firebaseConfig } from "./constants";
import * as firebase from "firebase/app";
import "firebase/auth";
import "firebase/firestore";
import store from "@/store";
import * as utils from "@/utils";

/*
 * AUTHENTICATION
 */
export async function initFirebase() {
  firebase.initializeApp(firebaseConfig);
}

export function authSetPersistence(persist) {
  const persistenceType = persist
    ? firebase.auth.Auth.Persistence.LOCAL
    : firebase.auth.Auth.Persistence.SESSION;
  firebase.auth().setPersistence(persistenceType);
}

export function authWithEmailLink(
  email,
  url,
  parameters = {},
  errorCallback = () => {}
) {
  parameters["loginlink"] = true;
  const actionCodeSettings = {
    url:
      baseUrl +
      (url.startsWith("/") ? url : "/" + url) +
      "?" +
      objToUrl(parameters),
    handleCodeInApp: true,
  };

  firebase
    .auth()
    .sendSignInLinkToEmail(email, actionCodeSettings)
    .then(function() {
      window.localStorage.setItem("emailForSignIn", email);
      window.localStorage.setItem("completeAuthWithEmailLink", "true");
    })
    .catch(function(error) {
      if (errorCallback) errorCallback(error);
    });
}

export function completeAuthWithEmailLink(callback, errorCallback) {
  if (firebase.auth().isSignInWithEmailLink(window.location.href)) {
    let email = window.localStorage.getItem("emailForSignIn") || "";
    if (!email) {
      email = window.prompt("Please provide your email for confirmation") || "";
    }
    firebase
      .auth()
      .signInWithEmailLink(email, window.location.href)
      .then((result) => {
        if (callback) callback(result);
        window.localStorage.removeItem("emailForSignIn");
        window.localStorage.removeItem("completeAuthWithEmailLink");
      })
      .catch((error) => {
        if (errorCallback) errorCallback(error);
      });
  }
}

export function authWithGoogle() {
  const provider = new firebase.auth.GoogleAuthProvider();
  provider.addScope("https://www.googleapis.com/auth/userinfo.email");
  firebase.auth().useDeviceLanguage();
  window.localStorage.setItem("completeAuthWithGoogle", "true");
  firebase.auth().signInWithRedirect(provider);
}

export function completeAuthWithGoogle(errorCallback) {
  window.localStorage.removeItem("completeAuthWithGoogle");
  firebase
    .auth()
    .getRedirectResult()
    .then(function(result) {
      if (result.credential) {
        store.commit("tokenGoogleMutation", result.credential.accessToken);
      }
    })
    .catch(function(error) {
      if (errorCallback) errorCallback(error);
    });
}

export function authWithPhone(
  phoneNumber,
  appVerifier,
  callback,
  errorCallback
) {
  firebase
    .auth()
    .signInWithPhoneNumber(phoneNumber, appVerifier)
    .then((confirmationResult) => {
      if (callback) callback();
      window.confirmationResult = confirmationResult;
    })
    .catch((error) => {
      if (errorCallback) errorCallback(error);
    });
}

export function completeAuthWithPhone(code, callback, errorCallback) {
  window.confirmationResult
    .confirm(code)
    .then((result) => {
      if (callback) callback(result);
    })
    .catch((error) => {
      if (errorCallback) errorCallback(error);
    });
}

export function signOut(callback, errorCallback) {
  firebase
    .auth()
    .signOut()
    .then((result) => {
      if (callback) callback(result);
    })
    .catch((error) => {
      if (errorCallback) errorCallback(error);
    });
}

export function startAuth() {
  let auth_url = "https://accounts.google.com/o/oauth2/auth?";
  let client_id =
    "631987309722-rpc0pflrliljve9cioq7bc2ssa86b5uc.apps.googleusercontent.com";
  let redirect_url = chrome.identity.getRedirectURL("oauth2");

  let auth_params = {
    client_id: client_id,
    redirect_uri: redirect_url,
    response_type: "token",
    scope: "https://www.googleapis.com/auth/userinfo.email",
  };

  const url = new URLSearchParams(Object.entries(auth_params));
  url.toString();
  auth_url += url;

  chrome.identity.launchWebAuthFlow(
    { url: auth_url, interactive: true },
    function(responseUrl) {
      let url = new URL(responseUrl);
      let token = url.hash.match(/\#(?:access_token)\=([\S\s]*?)\&/)[1];
      if (token) {
        // Authorize Firebase with the OAuth Access Token.
        let credential = firebase.auth.GoogleAuthProvider.credential(
          null,
          token
        );
        firebase
          .auth()
          .signInWithCredential(credential)
          .catch(function(error) {
            // The OAuth token might have been invalidated. Lets' remove it from cache.
            if (error.code === "auth/invalid-credential") {
              chrome.identity.removeCachedAuthToken(
                { token: token },
                function() {
                  startAuth(interactive);
                }
              );
            }
          });
      } else {
        console.error("Failed to get auth token");
      }
    }
  );
}

export function logOut() {
  firebase.auth().signOut();
}

export function getFbUser() {
  return firebase.auth().currentUser;
}

// Helper functions

export function validateEmail(email) {
  /* eslint-disable no-useless-escape */
  const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
}

export const initReCaptcha = (buttonId, callback, errorCallback) => {
  window.recaptchaVerifier = new firebase.auth.RecaptchaVerifier(buttonId, {
    size: "invisible",
    callback: callback,
    "expired-callback": errorCallback,
  });
  return window.recaptchaVerifier;
};

/*
 * DATA MANAGEMENT
 */

const sendMailCollectionPath = (userId) =>
  "userdata/" + userId + "/triggerSendEmail";

export function userSendMailCollection() {
  let currentUser = getFbUser();
  if (currentUser) {
    let currentUID = currentUser.uid;
    let collectionName = sendMailCollectionPath(currentUID);
    return firebase.firestore().collection(collectionName);
  } else {
    console.error({ "No user to write": docContent });
  }
}

export function writeUserMail(doc) {
  let docName = new Date().getTime().toString() + "_" + utils.randomString(5);
  return userSendMailCollection()
    .doc(docName)
    .set(doc);
}

export async function getUserDoc(docName) {
  let response = await userSendMailCollection()
    .doc(docName)
    .get();
  let result = response.exists ? response.data() : null;
  return result;
}

export async function deleteUserDoc(docName) {
  return await userSendMailCollection()
    .doc(docName)
    .delete();
}
